import { isEmpty, sortBy } from 'lodash'
import moment from 'moment'

import {
  type LabelKey,
  ProvenanceTargetType,
  TargetType
} from 'components/attacks/create/create-attack-types'
import { HeptagonGlyphIcon } from 'components/common/GlyphIcon'
import SquareGlyph from 'components/common/SquareGlyph'
import SvgHostname from 'components/icons/attacks/Hostname'
import SvgCube from 'components/icons/Cube'
import SvgHelm from 'components/icons/Helm'
import SvgStack from 'components/icons/Stack'
import {
  type Client,
  ClientStatus,
  type Container
} from 'types/api/attacks/client'
import {
  type InfraAttack,
  type Strategy
} from 'types/api/attacks/details/infra-attack'
import {
  type ContainerTargetingStrategy,
  type HostTargetingStrategy,
  InfraStrategyType,
  type KubernetesTargetingStrategy,
  type TargetingStrategy
} from 'types/api/attacks/details/targeting'
import { type K8sClient, type K8sObject } from 'types/api/kubernetes/kubernetes'
import {
  getClientVersion,
  getClientVersionArray,
  isOlderClientVersion,
  NEW_CLIENT_VERSION_MIN,
  NEW_WINDOWS_CLIENT_VERSION_MIN
} from 'utils/client-version'
import { unique } from 'utils/list'
import { hasKey } from 'utils/obj'
import { capitalize } from 'utils/strings'

export const TARGET_TYPES = {
  Host: {
    type: 'Host',
    label: 'Hosts',
    path: 'hosts',
    item: 'host',
    SvgIcon: SvgStack,
    SvgGlyphIcon: SquareGlyph
  },
  Container: {
    type: 'Container',
    label: 'Containers',
    path: 'containers',
    item: 'container',
    SvgIcon: SvgCube,
    SvgGlyphIcon: SquareGlyph
  },
  Kubernetes: {
    type: 'Kubernetes',
    label: 'Kubernetes',
    path: 'kubernetes',
    item: 'pod',
    SvgIcon: SvgHelm,
    SvgGlyphIcon: HeptagonGlyphIcon
  },
  Applications: {
    type: 'Applications',
    label: 'Applications',
    path: 'applications',
    item: 'application',
    SvgIcon: SvgHostname,
    SvgGlyphIcon: SquareGlyph
  }
}

/**
 * These targetType check functions are used in legacy components created before our Typescript migration where the
 * "Hosts" target type deviates from the consistent TargetType definitions
 */
export function isHost(targetType: string): boolean {
  return targetType === TARGET_TYPES.Host.type
}
export function isContainer(targetType: string): boolean {
  return targetType === TARGET_TYPES.Container.type
}
export function isKubernetes(
  targetType: TargetType | ProvenanceTargetType
): boolean {
  return targetType === TARGET_TYPES.Kubernetes.type
}

export function isActive(target: Client) {
  return target.state === 'ACTIVE'
}

export function getTargetType(targetType: TargetType) {
  if (targetType === TargetType.Hosts) {
    return TARGET_TYPES.Host.type
  }

  if (targetType === TargetType.Containers) {
    return TARGET_TYPES.Container.type
  }
  return targetType
}

export function isHostTarget(target: Client | Container): target is Client {
  return hasKey(target, 'identifier')
}

export function getUniqueTargetId(target: Client | Container) {
  return isHostTarget(target) ? target.identifier : target.id
}

export function getTargetDisplayName(target: Client | Container) {
  return isHostTarget(target) ? target.identifier : target.name
}

export function getTargetParams(target: Client | Container) {
  return isHostTarget(target) ? target.tags : target.labels
}

export function isHostStrategy(strategy: Strategy): strategy is Strategy {
  return strategy?.type === ProvenanceTargetType.ProcessData
}

export function isHostTargetingStrategy(
  targetingStrategy: TargetingStrategy | undefined
): targetingStrategy is HostTargetingStrategy {
  return targetingStrategy?.type === ProvenanceTargetType.ProcessData
}

export function isContainerTargetingStrategy(
  targetingStrategy: TargetingStrategy | undefined
): targetingStrategy is ContainerTargetingStrategy {
  return targetingStrategy?.type === ProvenanceTargetType.Container
}

export function isKubernetesTargetingStrategy(
  targetingStrategy: TargetingStrategy | undefined
): targetingStrategy is KubernetesTargetingStrategy {
  return targetingStrategy?.type === ProvenanceTargetType.Kubernetes
}

export function isTargetParamsEmpty(
  targetParams: Record<string, Set<string>>
): boolean {
  return (
    isEmpty(targetParams) ||
    Object.values(targetParams).every((paramValues) => {
      if (paramValues instanceof Set) {
        return paramValues.size === 0
      } else {
        return !paramValues
      }
    })
  )
}

export function getContainersFromClients(clients: Client[]): Container[] {
  if (clients) {
    return clients.flatMap((c) => c.containers)
  }

  return []
}

export function getAllTargetsIds(
  targets: Client[] | Container[] = [],
  targetType: TargetType
): string[] {
  switch (targetType) {
    case TargetType.Containers: {
      return targets.reduce((accum, { containers = [] }) => {
        Array.prototype.push.apply(
          accum,
          containers.map(({ id }) => id)
        )
        return accum
      }, [])
    }
    default: {
      return targets.map((client) => client.identifier)
    }
  }
}

export function getCurrentTargetsIds(
  clients: Client[] | Container[],
  targets: Client[] | Container[] = [],
  targetType: TargetType
) {
  if (targets.length === 0) return targets

  const allTargetsIds = getAllTargetsIds(clients, targetType)
  return targets.filter((target) => allTargetsIds.includes(target))
}

function getTagsAndLabelsAttributes(targetType: TargetType, targets: Client[]) {
  switch (targetType) {
    case TargetType.Containers:
      return {
        idKey: 'id',
        paramsKey: 'labels',
        availableTargets: getContainersFromClients(targets)
      }
    default:
      return {
        idKey: 'identifier',
        paramsKey: 'tags',
        availableTargets: targets
      }
  }
}

// Shape of labelKeys
/* {
    <key>: {
      clients: {
        <client1>: true/false
      },
      values: {
        <value1>: true/false
      }
    }
  } */

// Shape of labelKeys
/* {
    ['<key>:<value>', '<key2>:<value2>']
    }
  } */

// Shape of selectedSets
/* {
      <key>: <value>
    }
  } */
export const formatTagsAndLabels = (
  targetType: TargetType,
  selectedTargetParams: Record<string, Set<string>> = {},
  targets: Client[],
  selectedTargets: Client[] | Container[]
) => {
  const { idKey, paramsKey, availableTargets } = getTagsAndLabelsAttributes(
    targetType,
    targets
  )

  // Create singular set of alike tags/labels
  const combined = selectedTargets.reduce(
    (combinedParams: Record<string, Set<string>>, target) => {
      const params: Record<string, string> = target[paramsKey]
      Object.entries(params).forEach(([key, value]) => {
        if (!combinedParams[key]) {
          combinedParams[key] = new Set()
        }

        combinedParams[key].add(value)
      })

      return combinedParams
    },
    {}
  )

  const out = availableTargets.reduce(
    ({ keyValueSets, labelKeys }, target) => {
      const id = target[idKey]
      const params = target[paramsKey]

      Object.entries(params).forEach(([key, value]) => {
        if (!labelKeys[key]) {
          labelKeys[key] = {
            name: key,
            targets: [],
            values: new Set(),
            disabled: new Set()
          }
        }

        // Add key-value to the set
        keyValueSets.add(`${key}:"${value}"`)
        labelKeys[key].values.add(value)
        labelKeys[key].targets.push(id)

        // If no target params have been selected yet; skip adding anything to the disabled list
        if (isTargetParamsEmpty(selectedTargetParams)) {
          return
        }

        if (combined[key] && combined[key].has(value)) {
          labelKeys[key].disabled.delete(value)
        } else {
          labelKeys[key].disabled.add(value)
        }
      })
      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set(),
      labelKeys: {}
    }
  )
  return { availableTargetParams: out.keyValueSets, labelKeys: out.labelKeys }
}

export const isTargetSelected = (
  target: Client | Container,
  targetingStrategyType: string,
  targetType: TargetType,
  selectedTargets: string[] = [],
  selectedTargetParams: Record<string, Set<string>> = {},
  targetAll: boolean
) => {
  if (
    selectedTargets.length === 0 &&
    targetingStrategyType.toLowerCase() !== 'random'
  ) {
    return false
  }

  if (selectedTargets.length) {
    return selectedTargets.some((targetId) => {
      if (isHostTarget(target)) {
        return targetId === target.identifier
      } else {
        return targetId === target.id
      }
    })
  }

  const params = isHostTarget(target) ? target.tags : target.labels

  // If there are no target params with selected values, all targets are considered selected
  // But we have switched our UI to show none selected by default to "start safe"
  // This is overriden later on with the "Target all hosts" option
  // This creates a default state of no targets being selected
  if (targetAll) {
    if (isTargetParamsEmpty(selectedTargetParams)) return true
  } else {
    if (isTargetParamsEmpty(selectedTargetParams)) return false
  }
  return Object.entries(selectedTargetParams).every(
    ([key, values = new Set()]) => {
      if (values instanceof Set) {
        if (values.size === 0) return true // skip empty sets
        return values.has(params[key])
      } else {
        return params && params[key] === values
      }
    }
  )
}

export function getClientsForTargetingStrategy(
  infraClients: Client[] = [],
  targetingStrategy: TargetingStrategy = {} as TargetingStrategy
) {
  if (targetingStrategy?.type === 'Host') {
    if (targetingStrategy.isAll) {
      return infraClients
    } else if (targetingStrategy.ids?.length > 0) {
      return infraClients.filter((client) =>
        targetingStrategy.ids.includes(client.identifier)
      )
    } else if (targetingStrategy.multiSelectTags) {
      // multiSelect
      return infraClients.filter((client) =>
        Object.entries(targetingStrategy.multiSelectTags).every(
          ([key, values]) =>
            // Client must have the Key and match at least one value
            client.tags[key] && values.some((val) => client.tags[key] === val)
        )
      )
    }
  } else if (targetingStrategy?.type === 'Container') {
    if (targetingStrategy.isAll) {
      return infraClients
    } else if (targetingStrategy.ids?.length > 0) {
      return infraClients
        .map((client) => {
          return {
            ...client,
            containers: client.containers.filter((container) =>
              targetingStrategy.ids.includes(container.id)
            )
          }
        })
        .filter((client) => client.containers?.length > 0)
    } else if (targetingStrategy.multiSelectLabels) {
      // multiSelect
      return infraClients
        .map((client) => {
          return {
            ...client,
            containers: client.containers.filter((container) =>
              Object.entries(targetingStrategy.multiSelectLabels).every(
                ([key, values]) =>
                  // Client must have the Key and match at least one value
                  container.labels[key] &&
                  values.some((val) => container.labels[key] === val)
              )
            )
          }
        })
        .filter((client) => client.containers?.length > 0)
    }
  }
  return []
}

export function getK8sClientsForTargetingStrategy(
  k8sClientsRaw: K8sClient[],
  targetingStrategy: TargetingStrategy
): K8sClient[] {
  if (targetingStrategy?.type === 'Kubernetes') {
    if (targetingStrategy.isAll) {
      return k8sClientsRaw
    } else if (targetingStrategy.ids?.length) {
      return k8sClientsRaw
        .map((client) => {
          return {
            ...client,
            objects: client.objects?.filter((k8sObject) =>
              targetingStrategy.ids?.includes(k8sObject.uid)
            )
          }
        })
        .filter((client) => client.objects.length > 0)
    } else if (targetingStrategy.names?.length) {
      const clusterIds = unique(
        targetingStrategy.names
          .filter((entry) => Boolean(entry.clusterId)) // filter out null values
          .map((entry) => entry.clusterId)
      )
      return k8sClientsRaw
        .filter((client) =>
          clusterIds.length > 0 ? clusterIds.includes(client.clusterId) : true
        )
        .map((client) => {
          const matchingObjects = client.objects?.filter((k8sObject) =>
            targetingStrategy.names?.some(
              (entry) =>
                entry.name === k8sObject.name &&
                entry.namespace === k8sObject.namespace &&
                entry.kind === k8sObject.kind
            )
          )
          const descendants = matchingObjects.flatMap((k8sObject) =>
            getDescendantK8sObjects(k8sObject, client.objects)
          )
          return {
            ...client,
            objects: [...matchingObjects, ...descendants]
          }
        })
    }
  }
  return []
}

/**
 * Recursively follow ownerReferences to get all child objects of a k8s object
 * k8s object ownership is a tree, with at least two levels of ownership:
 * Deployment -> ReplicaSet -> Pod
 */
function getDescendantK8sObjects(parentObject, k8sObjects) {
  const descendants = k8sObjects.filter(
    (k8sObject) => k8sObject.ownerReferences === parentObject.uid
  )
  descendants.forEach((descendant) => {
    descendants.push(...getDescendantK8sObjects(descendant, k8sObjects))
  })
  return descendants
}

/**
 * Returns the clients that would be targeted for either host or container based attacks
 */
export function getSelectedClients(
  clients: Client[] | Container[],
  targetingStrategyType: string,
  targetType: TargetType,
  selectedTargets: string[],
  targetParams: Record<string, Set<string>>,
  targetAll: boolean
) {
  return clients.reduce((accum, target) => {
    if (targetType === TargetType.Hosts) {
      const isClientSelected = isTargetSelected(
        target,
        targetingStrategyType,
        targetType,
        selectedTargets,
        targetParams,
        targetAll
      )
      if (isClientSelected) {
        accum.push(target)
      }
    } else if (targetType === TargetType.Containers) {
      const { containers } = target
      for (let i = 0; i < containers.length; ++i) {
        const container = containers[i]
        const isContainerSelected = isTargetSelected(
          container,
          targetingStrategyType,
          targetType,
          selectedTargets,
          targetParams,
          targetAll
        )

        if (isContainerSelected) {
          accum.push(target)
          break
        }
      }
    }
    return accum
  }, [])
}

/**
 * Returns the relevent selected targets for host or container attacks
 * if the targetType is Hosts then this will return clients
 * else if targetType is Containers it will return the targeted containers
 */
export function getSelectedTargets(
  availableTargets: Client[] | Container[],
  targetingStrategyType: string,
  targetType: TargetType,
  selectedTargets: string[],
  targetParams: Record<string, Set<string>>,
  targetAll: boolean
) {
  const targets =
    targetType === TargetType.Hosts
      ? availableTargets
      : getContainersFromClients(availableTargets)

  return targets.filter((target) =>
    isTargetSelected(
      target,
      targetingStrategyType,
      targetType,
      selectedTargets,
      targetParams,
      targetAll
    )
  )
}

// Get total count of client available by targetParams selected
export function getTotalSelectedClients(...args) {
  return getSelectedClients(...args).length
}

/**
 * Sort labels by order in defaults and number of values, then
 * split labels into 'displayed' and 'others' for UI
 */
export function sortSelectableTargetParams(
  labelKeys: Record<string, LabelKey>,
  defaults: { key: string; alwaysShowIfPopulated?: boolean }[],
  numberOfSections: number
) {
  const sortedLabelsByClientsLength = sortBy(Object.keys(labelKeys), [
    // sort by defaults and then by number of unique values
    (label) => {
      // Using the findIndex we can ensure that if a label is a default it will
      // have a positive number and if not it will be `-1`
      return defaults.findIndex(({ key }) => key === label)
    },
    (label) => {
      // sort by the amount of unique values
      return labelKeys[label].values.size
    }
  ])
    // lodash sortBy is ascending so we reverse it so the "highest" are first
    .reverse()

  return sortedLabelsByClientsLength.reduce(
    (
      {
        displayed,
        others
      }: {
        displayed: string[]
        others: string[]
      },
      labelKey
    ) => {
      const currentLabelObj = labelKeys[labelKey]

      // Label keys with 2 or more values
      const canDisplayCurrentLabel = currentLabelObj.values.size > 1

      // Default label keys
      const labelKeyInDefaults = defaults.find(({ key }) => key === labelKey)

      // Make sure we have one spot left over for "others"
      const hasRoomForOthers = displayed.length < numberOfSections - 1

      if (
        (Boolean(labelKeyInDefaults) || canDisplayCurrentLabel) &&
        (labelKeyInDefaults?.alwaysShowIfPopulated || hasRoomForOthers)
      ) {
        displayed.push(labelKey)
      } else {
        others.push(labelKey)
      }

      return {
        displayed,
        others
      }
    },
    {
      displayed: [],
      others: []
    }
  )
}

/**
 * This is used to determine the number of targets that will be impacted based on a percentage being supplied
 * @NOTE Only for use with percentage impacts
 */
export function getActualTargetsImpacted(
  totalSelectedTargets: number,
  percentage: number
) {
  if (totalSelectedTargets === 0) {
    return 0
  }

  return Math.floor((percentage / 100) * totalSelectedTargets)
}

/**
 * Returns correct tag/label format based on the type of selection strategy being used
 * @NOTE Currently based on `multiselection-targeting-enabled` LD feature flag
 */
export function getTargetParamsKey(
  targetType: TargetType | ProvenanceTargetType,
  targetingStrategyType: InfraStrategyType
) {
  // Cleanup in attempt to catch any lowercase entries into this file
  const lowerTargetType = targetType.toLowerCase()

  if (targetingStrategyType === InfraStrategyType.Random) {
    if (lowerTargetType === 'hosts' || lowerTargetType === 'host') {
      return 'multiSelectTags'
    } else {
      return 'multiSelectLabels'
    }
  } else if (
    lowerTargetType === 'containers' ||
    lowerTargetType === 'container'
  ) {
    return 'labels'
  } else {
    return 'tags'
  }
}

export function convertParamsToSets(
  targetParams: Record<string, string[]> = {}
) {
  return Object.entries(targetParams).reduce((params, [key, value]) => {
    if (Array.isArray(value)) {
      params[key] = new Set(value)
    } else {
      params[key] = new Set([value])
    }

    return params
  }, {})
}

export function getTargetsWithDeprecatedVersion(
  clients: Client[] | Container[] = []
) {
  return clients.reduce((accum, target) => {
    const clientVersionArray = getClientVersionArray(getClientVersion(target))
    if (
      isOlderClientVersion(
        clientVersionArray,
        target.os_type === 'Linux'
          ? NEW_CLIENT_VERSION_MIN
          : NEW_WINDOWS_CLIENT_VERSION_MIN
      )
    ) {
      accum.push(target)
    }
    return accum
  }, [])
}

export function getLabelDisplayName(name: string) {
  if (name) {
    switch (name) {
      case 'os-type':
        return 'Operating Systems'
      case 'service':
      case 'zone':
      case 'region':
        return capitalize(name)
      default:
        return name
    }
  }

  return ''
}

export function getClientsLastAttackedDateMap(
  attacks: InfraAttack[] = [],
  clients: Client[] = []
) {
  if (attacks.length === 0 || clients.length === 0) return {}

  const dateMap = {}
  attacks.forEach((attack) => {
    if (attack.targets) {
      attack.targets.reduce((accum, targetId) => {
        if (!accum[targetId]) {
          accum[targetId] = attack.start_time
        } else if (moment(attack.start_time).isAfter(accum[targetId])) {
          accum[targetId] = attack.start_time
        }
        return accum
      }, dateMap)
    }
  })

  return dateMap
}

export function isDockerClient(client: Client) {
  if (client) {
    return 'docker_version' in client
  }

  return false
}

export function getK8sObjectContainerNames(k8Object: K8sObject = {}) {
  if (!k8Object.availableContainers) {
    return []
  }
  // get the list of names and filter out container without name prop
  return k8Object.availableContainers
    .map((obj) => obj.name)
    .filter((obj) => obj)
}

// note: this should eventually be changed to `isTargetHealthy`, but we currently can only be certain of an unhealthy state
export function isTargetUnhealthy(target: Client) {
  return target.validation_results?.client_status === ClientStatus.UNHEALTHY
}
